// src/pages/ChangePasswordPage.js
import axios from '../utils/axiosConfig';
import React, { useMemo, useState } from 'react';
import { useParams, useNavigate} from 'react-router-dom';
import '../Style/ChangePasswordPage.css';

export default function ChangePasswordPage() {
  const { uid, token } = useParams();
  const isConfirm = Boolean(uid && token);
  const navigate = useNavigate();

  // A) request link while logged-in
  const [currentPassword, setCurrentPassword] = useState('');
  // B) set new password via email link
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const rules = useMemo(() => ({
    min8: newPassword.length >= 8 && newPassword.length <= 128,
    lower: /[a-z]/.test(newPassword),
    upper: /[A-Z]/.test(newPassword),
    digit: /[0-9]/.test(newPassword),
    special: /[^A-Za-z0-9]/.test(newPassword),
    match: newPassword.length > 0 && newPassword === confirmPassword,
  }), [newPassword, confirmPassword]);

  const validatePassword = () => {
    if (newPassword.length > 128) return 'Password cannot exceed 128 characters.';
    if (!rules.min8) return 'Password must be at least 8 characters.';
    if (!rules.lower) return 'Include at least one lowercase letter.';
    if (!rules.upper) return 'Include at least one uppercase letter.';
    if (!rules.digit) return 'Include at least one number.';
    if (!rules.special) return 'Include at least one special character.';
    if (!rules.match) return 'Passwords do not match.';
    return '';
  };

  // STEP A — verify current password & send reset link (logged-in user)
  const handleRequestLink = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!currentPassword) {
      setError('Please enter your current password.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/reset-password/`,
        { current_password: currentPassword, frontend_url: window.location.origin },
        { headers: { 'X-Skip-Auth-Redirect': '1' } }
      );
      setCurrentPassword('');
      setSuccess('Password reset link sent to your email. Please check your inbox.');
    } catch (err) {
      const st = err?.response?.status;
      const msg = err?.response?.data?.error;
      if (st === 403) setError(msg || 'Incorrect current password.');
      else if (st === 400) setError(msg || 'Missing current password or frontend URL.');
      else if (st === 500) setError('Failed to send email. Please try again later.');
      else setError('Unable to process your request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // STEP B — confirm & set new password (via email link)
  const handleConfirm = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');

    const clientErr = validatePassword();
    if (clientErr) { setError(clientErr); return; }

    setLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/reset-password-confirm/${uid}/${token}/`,
        { uid, token, password: newPassword },
        { headers: { 'X-Skip-Auth-Redirect': '1' } }
      );

      // Success: wipe auth & go to login (AuthSync now treats this path as public)
      localStorage.removeItem('token');
      localStorage.removeItem('user_type');
      setSuccess('Password reset successful. Redirecting to login…');
      setTimeout(() => navigate('/login', { replace: true }), 2000);

    } catch (err) {
      // Show API’s precise reasons (token invalid/expired, password reuse, serializer errors)
      const api = err?.response?.data;
      const msg =
        api?.error ||
        (typeof api === 'object' ? Object.values(api).flat().join(' ') : '') ||
        'Reset failed. The link may be invalid or expired.';

      // Normalize reuse message
      if (/reuse|old passwords/i.test(msg)) {
        setError("You can't reuse your recent passwords (last 9).");
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="change-password-container">
      <div className="change-password-box">

        <h2>{isConfirm ? 'Set New Password' : 'Change Your Password'}</h2>

        {error   && <p className="error-message" role="alert">{error}</p>}
        {success && <p className="success-message">{success}</p>}

        {!isConfirm && (
          <form onSubmit={handleRequestLink} className="change-password-form" autoComplete="off">
            <label htmlFor="current-password">Current Password</label>
            <input
              id="current-password"
              type="password"
              name="cpwd"
              value={currentPassword}
              onChange={(e) => { setCurrentPassword(e.target.value); setError(''); setSuccess(''); }}
              required
              placeholder="Enter your current password"
              autoComplete="off"
            />
            <button type="submit" disabled={loading}>
              {loading ? 'Sending…' : 'Email Me a Reset Link'}
            </button>
          </form>
        )}

        {isConfirm && (
          <form onSubmit={handleConfirm} className="change-password-form" autoComplete="off">
            <label htmlFor="new-password">New Password</label>
            <input
              id="new-password"
              type="password"
              name="npwd"
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
              required
              placeholder="Enter new password"
              autoComplete="new-password"
            />

            <label htmlFor="confirm-password">Confirm New Password</label>
            <input
              id="confirm-password"
              type="password"
              name="npwd_confirm"
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
              required
              placeholder="Re-enter new password"
              autoComplete="new-password"
            />

            {/* tiny inline checklist (optional, style in your CSS) */}
            <ul className="pw-req-list">
              <li className={rules.min8 ? 'ok' : ''}>At least 8 characters</li>
              <li className={rules.lower ? 'ok' : ''}>Lowercase letter</li>
              <li className={rules.upper ? 'ok' : ''}>Uppercase letter</li>
              <li className={rules.digit ? 'ok' : ''}>Number</li>
              <li className={rules.special ? 'ok' : ''}>Special character</li>
              <li className={rules.match ? 'ok' : ''}>Passwords match</li>
            </ul>

            <button type="submit" disabled={loading}>
              {loading ? 'Submitting…' : 'Reset Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
